/** Verified primary sources and applicability caveats:
 * docs/operations/clause-verifications/compliance-only-duties-2026-09-30.md.
 * Pure reminders only: task completion is never regulator-submission evidence.
 */
import { dateOnlyInTz, dayBoundsInTz } from '../timezone';
import { addCalendarDays } from '../compliance-calendar/date-only';
import type { ComplianceDeadlineItem } from './types';

export const DUTY_VERIFICATION_ARTIFACT =
  'docs/operations/clause-verifications/compliance-only-duties-2026-09-30.md';

export const CHANGE_NOTICE_TRIGGERS = {
  PROVIDER_SUITABILITY: 'Provider suitability',
  RESPONSIBLE_PERSON_SUITABILITY: 'Responsible person suitability',
  RESPONSIBLE_PERSON_CHANGE: 'Responsible person joining or leaving',
  ORGANISATION_ADMINISTRATION: 'Organisation arrangements, administration or insolvency',
  LGA_OR_SCALE: 'Service local government area or significant worker-number change',
  SERVICE_TYPES: 'Intended service types',
  FINANCIAL_CAPACITY: 'Financial capacity (excluding government entities)',
} as const;

export const PROVIDER_RECURRING_DUTIES = {
  INCIDENT_TRAINING: {
    title: 'Incident management training',
    months: 12,
    provision: 'r 164-35(2)',
  },
  COMPLAINTS_REVIEW: { title: 'Complaints system review', months: 12, provision: 'r 165-15(1)(p)' },
  COMPLAINTS_TRAINING: { title: 'Complaints training', months: 12, provision: 'r 165-20(2)' },
  COMPLAINTS_WELCOME: {
    title: 'Complaints welcome communication',
    months: 1,
    provision: 'r 165-20(1)(j)',
  },
} as const;
export type RecurringProviderDuty = keyof typeof PROVIDER_RECURRING_DUTIES;
export type ProviderDutyType =
  | 'COMPLAINTS_ANNUAL_REVIEW'
  | 'COMPLAINTS_REQUEST'
  | 'CHANGE_NOTICE'
  | 'WHS_WRITTEN'
  | RecurringProviderDuty;
export interface ProviderDutyReminder {
  type: ProviderDutyType;
  anchor: string;
  changeTrigger?: keyof typeof CHANGE_NOTICE_TRIGGERS;
  jurisdiction?: 'QLD' | 'VIC';
  /** Qld: a regulator requirement is mandatory. Vic: awareness starts the clock. */
  regulatorRequested?: boolean;
  /** r 166-220 longer requester period; annual reviews use a provider scheduling target. */
  specifiedDueOn?: string;
}

function localDay(value: string, timezone: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    if (addCalendarDays(value, 0) !== value) throw new Error('A valid calendar date is required.');
    return value;
  }
  // A timestamp must carry its offset; browser/server local time is not authority.
  if (!/(Z|[+-]\d{2}:\d{2})$/.test(value) || !Number.isFinite(Date.parse(value)))
    throw new Error('A date or timestamp with a timezone is required.');
  return dateOnlyInTz(new Date(value), timezone);
}
function addMonths(day: string, months: number): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (
    !match ||
    match[1] === undefined ||
    match[2] === undefined ||
    match[3] === undefined ||
    addCalendarDays(day, 0) !== day
  )
    throw new Error('A valid calendar date is required.');
  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  const end = new Date(Date.UTC(y, m - 1 + months + 1, 0));
  const date = new Date(Date.UTC(y, m - 1 + months, Math.min(d, end.getUTCDate())));
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
}
export function providerDutyItem(input: {
  id: string;
  recordRef: string;
  title: string;
  dueOn: string;
  provision: string;
  timezone: string;
  now: Date;
  href: string;
  kind?: ComplianceDeadlineItem['kind'];
}): ComplianceDeadlineItem {
  const today = dateOnlyInTz(input.now, input.timezone);
  return {
    id: input.id,
    register: 'PROVIDER_DUTIES',
    recordRef: input.recordRef,
    title: input.title,
    dueAt: dayBoundsInTz(input.dueOn, input.timezone).endUtc,
    kind: input.kind ?? 'STATUTORY',
    provisionOrTargetLabel: input.provision,
    status:
      input.dueOn < today
        ? 'OVERDUE'
        : input.dueOn <= addCalendarDays(today, 14)
          ? 'DUE_SOON'
          : 'UPCOMING',
    href: input.href,
    meta: {
      dueOn: input.dueOn,
      verificationArtifact: DUTY_VERIFICATION_ARTIFACT,
      reminderOnly: true,
    },
  };
}

/** B1: five calendar days after the start of the P1 24-hour window.
 * Commissioner waiver/other period must be recorded separately; no inference from initial lodgement.
 */
export function evaluateSirsPriorityOneFollowUp(
  row: {
    id: string;
    awarenessAt: string;
    specifiedDueOn?: string;
    waived?: boolean;
    submitted?: boolean;
  },
  now: Date,
  timezone: string,
): ComplianceDeadlineItem | null {
  if (row.waived || row.submitted) return null;
  const dueOn = row.specifiedDueOn
    ? localDay(row.specifiedDueOn, timezone)
    : addCalendarDays(localDay(row.awarenessAt, timezone), 5);
  return {
    ...providerDutyItem({
      id: `sirs-p1-follow-up:${row.id}`,
      recordRef: row.id,
      title: 'SIRS Priority 1 additional information',
      dueOn,
      provision: 'Aged Care Rules 2025 r 165A-25(6)–(7)',
      now,
      timezone,
      href: `/compliance/incidents/${row.id}`,
    }),
    register: 'SIRS_NOTIFICATION',
  };
}

export function evaluateProviderDutyReminder(
  recordId: string,
  reminder: ProviderDutyReminder,
  now: Date,
  timezone: string,
  provider: { categories: readonly string[] | null; confirmed: boolean } = {
    categories: null,
    confirmed: false,
  },
): ComplianceDeadlineItem {
  const anchor = localDay(reminder.anchor, timezone);
  let dueOn: string;
  let title: string;
  let provision: string;
  let kind: ComplianceDeadlineItem['kind'] = 'STATUTORY';
  if (reminder.type === 'WHS_WRITTEN') {
    if (
      !['QLD', 'VIC'].includes(reminder.jurisdiction ?? '') ||
      (reminder.jurisdiction === 'QLD' && !reminder.regulatorRequested)
    )
      throw new Error(
        'Qld written notice needs a regulator request; other jurisdictions remain unverified.',
      );
    if (!/(Z|[+-]\d{2}:\d{2})$/.test(reminder.anchor))
      throw new Error('WHS requires the awareness/request instant.');
    const dueAt = new Date(Date.parse(reminder.anchor) + 48 * 3600000).toISOString();
    return {
      id: `provider-duty:${recordId}`,
      register: 'PROVIDER_DUTIES',
      recordRef: recordId,
      title:
        reminder.jurisdiction === 'VIC'
          ? 'WHS written record — Victoria'
          : 'WHS requested written notice — Queensland',
      dueAt,
      kind: 'STATUTORY',
      provisionOrTargetLabel:
        reminder.jurisdiction === 'VIC'
          ? 'Occupational Health and Safety Act 2004 (Vic) s 38(3)'
          : 'Work Health and Safety Act 2011 (Qld) s 38(4)(b)',
      status: Date.parse(dueAt) < now.getTime() ? 'OVERDUE' : 'DUE_SOON',
      href: `/tasks/${recordId}`,
      meta: { reminderOnly: true, verificationArtifact: DUTY_VERIFICATION_ARTIFACT },
    };
  }
  if (reminder.type === 'COMPLAINTS_ANNUAL_REVIEW') {
    if (!anchor.endsWith('-06-30') && !reminder.specifiedDueOn)
      throw new Error('Enter a provider review target date for a different reporting period.');
    dueOn = reminder.specifiedDueOn
      ? localDay(reminder.specifiedDueOn, timezone)
      : `${anchor.slice(0, 4)}-10-31`;
    title = 'Review annual complaints and feedback report (governing-body signed)';
    provision =
      'Statura operational target — review reporting period, signature and submission evidence (Aged Care Rules 2025 rr 166-205, 166-210); 31 October only for July–June';
    kind = 'TARGET';
  } else if (reminder.type === 'COMPLAINTS_REQUEST') {
    const defaultDue = addCalendarDays(anchor, 14);
    dueOn = reminder.specifiedDueOn ? localDay(reminder.specifiedDueOn, timezone) : defaultDue;
    if (dueOn < defaultDue)
      throw new Error('A requester-specified period must be longer than 14 days.');
    title = 'Regulator-requested complaints report (governing-body signed)';
    provision = 'Aged Care Rules 2025 r 166-220';
  } else if (reminder.type === 'CHANGE_NOTICE') {
    if (!reminder.changeTrigger || !(reminder.changeTrigger in CHANGE_NOTICE_TRIGGERS))
      throw new Error('Select the change of circumstances.');
    dueOn = addCalendarDays(anchor, 14);
    title = `Change notice — ${CHANGE_NOTICE_TRIGGERS[reminder.changeTrigger]}`;
    provision = 'Aged Care Act 2024 s 167(4)(a); Aged Care Rules 2025 rr 167-10 to 167-60';
  } else {
    const duty = PROVIDER_RECURRING_DUTIES[reminder.type];
    if (!duty) throw new Error('Select a verified duty.');
    dueOn = addMonths(anchor, duty.months);
    title = duty.title;
    provision = `Aged Care Rules 2025 ${duty.provision} (at least ${duty.months === 1 ? 'monthly' : 'annually'}; earlier start/change duties also apply)`;
    const applies = recurringProviderDutyApplicability(
      reminder.type,
      provider.categories,
      provider.confirmed,
    );
    if (applies !== true) {
      kind = 'TARGET';
      title += applies === false ? ' — provider policy (AT-HM only)' : ' — confirm applicability';
      provision = `Statura operational target — ${applies === false ? 'IMS statutory training does not apply to AT-HM-only providers' : 'provider applicability needs confirmation'}; ${provision}`;
    }
  }
  return providerDutyItem({
    id: `provider-duty:${recordId}`,
    recordRef: recordId,
    title,
    dueOn,
    provision,
    kind,
    now,
    timezone,
    href: `/tasks/${recordId}`,
  });
}

/** Never auto-exclude unconfirmed/unknown categories. Numeric mapping uses the
 * existing canonical provider catalogue; the verification artifact does not verify that mapping.
 */
export function providerCategoryApplicability(
  categories: readonly string[] | null,
  confirmed: boolean,
  allowed: readonly string[],
): boolean | 'unknown' {
  if (
    !confirmed ||
    !categories?.length ||
    categories.some((c) => !['1', '2', '3', '4', '5', '6', 'SUBSCRIPTION_TRIAL'].includes(c))
  )
    return 'unknown';
  if (categories.some((c) => allowed.includes(c))) return true;
  return categories.includes('SUBSCRIPTION_TRIAL') ? 'unknown' : false;
}

/** Shared by generated schedules, saved reminders and their form preview. */
export function recurringProviderDutyApplicability(
  type: RecurringProviderDuty,
  categories: readonly string[] | null,
  confirmed: boolean,
): boolean | 'unknown' {
  return type === 'INCIDENT_TRAINING'
    ? providerCategoryApplicability(categories, confirmed, ['1', '3', '4', '5', '6'])
    : true;
}

export function generateVerifiedProviderDuties(input: {
  tenantId: string;
  start: string;
  end: string;
  timezone: string;
  now: Date;
  categories: readonly string[] | null;
  confirmed: boolean;
  /** Recorded anchors replace arbitrary schedules, including completed review tasks.
   * This reconciles reminders only; task completion is never legal submission. */
  recordedReminders?: readonly ProviderDutyReminder[];
}): ComplianceDeadlineItem[] {
  const items: ComplianceDeadlineItem[] = [];
  const annual = providerCategoryApplicability(input.categories, input.confirmed, ['4', '5', '6']);
  const ims = recurringProviderDutyApplicability(
    'INCIDENT_TRAINING',
    input.categories,
    input.confirmed,
  );
  const recorded = input.recordedReminders ?? [];
  const push = (
    id: string,
    title: string,
    dueOn: string,
    provision: string,
    kind: ComplianceDeadlineItem['kind'],
    applies: boolean | 'unknown',
  ) => {
    if (applies === false || dueOn < input.start || dueOn > input.end) return;
    const item = providerDutyItem({
      id,
      recordRef: input.tenantId,
      title: applies === 'unknown' ? `${title} — confirm applicability` : title,
      dueOn,
      provision,
      kind,
      ...input,
      href: '/compliance/work#provider-duties',
    });
    item.meta = {
      ...item.meta,
      organisationLevel: true,
      ...(applies === 'unknown'
        ? { applicabilityHint: 'Provider applicability needs confirmation' }
        : {}),
    };
    items.push(item);
  };
  for (let y = Number(input.start.slice(0, 4)); y <= Number(input.end.slice(0, 4)); y++) {
    if (
      !recorded.some(
        (r) =>
          r.type === 'COMPLAINTS_ANNUAL_REVIEW' &&
          localDay(r.anchor, input.timezone).startsWith(`${y}-`),
      )
    )
      push(
        `complaints-annual:${input.tenantId}:${y}`,
        'Review annual complaints and feedback report (governing-body signed)',
        `${y}-10-31`,
        'Statura operational target — confirm reporting period, applicability, governing-body signature and submission; 31 October for July–June (Aged Care Rules 2025 rr 166-205, 166-210); first-period transition unverified',
        'TARGET',
        annual,
      );
    for (const [key, duty] of Object.entries(PROVIDER_RECURRING_DUTIES)) {
      // A recorded last-completed anchor owns this schedule, even after its task is completed.
      if (recorded.some((r) => r.type === key)) continue;
      if (duty.months === 1) {
        for (let m = 1; m <= 12; m++) {
          const due = addCalendarDays(
            `${m === 12 ? y + 1 : y}-${String(m === 12 ? 1 : m + 1).padStart(2, '0')}-01`,
            -1,
          );
          push(
            `provider-cadence:${input.tenantId}:${key}:${y}-${m}`,
            duty.title,
            due,
            `Statura operational target — month-end scheduling; statutory minimum monthly (${duty.provision})`,
            'TARGET',
            true,
          );
        }
      } else
        push(
          `provider-cadence:${input.tenantId}:${key}:${y}`,
          duty.title,
          `${y}-06-30`,
          `Statura operational target — annual scheduling date; statutory minimum annual (${duty.provision}); record last completion for the interval clock`,
          'TARGET',
          key === 'INCIDENT_TRAINING' ? ims : true,
        );
    }
  }
  return items;
}
